/**
 * DeliveryAddressPage - Shows all addresses with edit and primary actions
 */

import React, { useState, useMemo } from "react";
import { Box, Divider, Stack, Typography, Chip } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StarIcon from "@mui/icons-material/Star";

import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QuizButton } from "../components/shared/QuizButton";
import { EditAddressModal } from "../components/EditAddressModal";
import { useAccount } from "../context/AccountContext";
import {
  updateAddressFields,
  // mergeAddresses as mergeAddressesApi,
  type AddressData,
} from "../api/accountProxy";
import { ACCOUNT_COLORS } from "../theme/colors";
import { QUIZ } from "@/theme/quizTokens";

type AddressLike = {
  id: string | number;
  first_name?: string;
  last_name?: string;
  address1?: string;
  address2?: string | null;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  phone?: string | null;
};

function clean(v: any): string {
  const s = String(v ?? "").trim();
  return s;
}

function formatLine(parts: Array<string | undefined | null>) {
  return parts
    .map((p) => clean(p))
    .filter(Boolean)
    .join(", ");
}

export function DeliveryAddressPage({
  orders,
  upcoming,
}: {
  orders: any;
  upcoming: any;
}) {
  const {
    addresses,
    subscriptions,
    showLoading,
    hideLoading,
    refresh,
    effectiveProxyBase,
    effectiveShop,
    customer,
  } = useAccount();

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAddress, setEditAddress] = useState<AddressLike | null>(null);

  // Normalize addresses from bootstrap
  const normalizedAddresses = useMemo(() => {
    if (!addresses) return [];
    const list = Array.isArray(addresses)
      ? addresses
      : (addresses as any).addresses || [];
    return list;
  }, [addresses]);

  // Calculate subscription counts for each address
  const addressesWithCounts = useMemo(() => {
    return normalizedAddresses.map((addr: any) => {
      const subs = subscriptions.filter(
        (sub: any) => String(sub.address_id) === String(addr.id)
      );
      const activeCount = subs.filter((s: any) => s.status === "ACTIVE").length;
      return {
        ...addr,
        subscriptionCount: subs.length,
        activeSubscriptionCount: activeCount,
      };
    });
  }, [normalizedAddresses, subscriptions]);

  // Find the primary address (one with most active subscriptions)
  const primaryAddressId = useMemo(() => {
    if (addressesWithCounts.length === 0) return null;
    const sorted = [...addressesWithCounts].sort(
      (a, b) => b.activeSubscriptionCount - a.activeSubscriptionCount
    );
    return sorted[0]?.activeSubscriptionCount > 0 ? String(sorted[0].id) : null;
  }, [addressesWithCounts]);

  // Handlers
  const handleOpenEditModal = (address: AddressLike) => {
    setEditAddress(address);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditAddress(null);
  };

  const handleSaveAddress = async (addressId: string, addressData: AddressData) => {
    showLoading("Updating your address...");
    try {
      const result = await updateAddressFields(
        effectiveProxyBase,
        effectiveShop,
        addressId,
        addressData,
        customer?.id ? String(customer.id) : undefined
      );

      if (!result.ok) {
        throw new Error(result.error || "Failed to update address");
      }

      await refresh();
    } finally {
      hideLoading();
    }
  };

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <QuizStyledCard>
        <Box sx={{ mb: 3 }}>
          <PageTitle
            title="Delivery Addresses"
            subtitle="Manage where your boxes are delivered"
          />
        </Box>

        {/* No Addresses */}
        {addressesWithCounts.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "50%",
                backgroundColor: ACCOUNT_COLORS.coralLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <HomeIcon
                sx={{ fontSize: { xs: 40, md: 50 }, color: ACCOUNT_COLORS.primary }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 800,
                color: ACCOUNT_COLORS.textPrimary,
                mb: 1,
              }}
            >
              No addresses found
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: ACCOUNT_COLORS.textPrimary,
                opacity: 0.75,
              }}
            >
              Addresses will appear here once you have an active subscription
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {addressesWithCounts.map((address: any) => {
              const isPrimary = String(address.id) === primaryAddressId;
              const hasSubscriptions = address.activeSubscriptionCount > 0;

              return (
                <QuizStyledCard
                  key={String(address.id)}
                  variant="white"
                  sx={{
                    border: isPrimary
                      ? `2px solid ${ACCOUNT_COLORS.primary}`
                      : `1px solid ${ACCOUNT_COLORS.borderLight}`,
                    position: "relative",
                  }}
                >
                  {/* Primary Badge */}
                  {isPrimary && (
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 14 }} />}
                      label="Primary Address"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -12,
                        left: 16,
                        height: 24,
                        background: ACCOUNT_COLORS.primary,
                        color: QUIZ.color.white,
                        fontWeight: 700,
                        fontSize: 11,
                        "& .MuiChip-icon": { color: QUIZ.color.white },
                      }}
                    />
                  )}

                  <Box sx={{ pt: isPrimary ? 1 : 0 }}>
                    {/* Address Content */}
                    <Box sx={{ mb: 2 }}>
                      {/* Name */}
                      <Typography
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          fontWeight: 800,
                          color: ACCOUNT_COLORS.textPrimary,
                          mb: 0.5,
                        }}
                      >
                        {formatLine([address.first_name, address.last_name]) || "Unnamed"}
                      </Typography>

                      {/* Address Lines */}
                      <Typography
                        sx={{
                          fontSize: { xs: 14, md: 15 },
                          color: ACCOUNT_COLORS.textPrimary,
                          opacity: 0.85,
                          lineHeight: 1.6,
                        }}
                      >
                        {formatLine([address.address1, address.address2]) || "—"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: 14, md: 15 },
                          color: ACCOUNT_COLORS.textPrimary,
                          opacity: 0.85,
                          lineHeight: 1.6,
                        }}
                      >
                        {formatLine([address.city, address.province, address.zip]) || "—"}
                      </Typography>

                      {/* Phone */}
                      {clean(address.phone) && (
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: ACCOUNT_COLORS.textTertiary,
                            mt: 1,
                          }}
                        >
                          {clean(address.phone)}
                        </Typography>
                      )}
                    </Box>

                    {/* Subscription Count */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={
                          address.activeSubscriptionCount > 0
                            ? `${address.activeSubscriptionCount} active subscription${
                                address.activeSubscriptionCount !== 1 ? "s" : ""
                              }`
                            : "No active subscriptions"
                        }
                        size="small"
                        sx={{
                          height: 26,
                          background: hasSubscriptions
                            ? ACCOUNT_COLORS.green
                            : ACCOUNT_COLORS.borderLight,
                          color: hasSubscriptions
                            ? ACCOUNT_COLORS.textPrimary
                            : ACCOUNT_COLORS.textTertiary,
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1.5}>
                      <QuizButton
                        variant="secondary"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => handleOpenEditModal(address)}
                      >
                        Edit
                      </QuizButton>
                    </Stack>
                  </Box>
                </QuizStyledCard>
              );
            })}
          </Stack>
        )}
      </QuizStyledCard>

      {/* Edit Address Modal */}
      <EditAddressModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        address={editAddress}
        onSave={handleSaveAddress}
      />

    </Stack>
  );
}
